import { useState } from 'react'

import { Wizard, WizardStep } from '@factorialco/deprecated-design-system'
import { Currency } from 'dinero.js'
import {
  ApiCoreAccess,
  ApiCoreCompany,
  EmployeesEmployee,
  PayrollPayrollSummaryImport,
  PayrollPolicyFragment,
  PayrollPolicyPeriodFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import { isCurrency } from 'modules/core/lib/money'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'

import MapToConceptsStep from './_steps/mapToConceptsStep'
import ReviewAndImportStep from './_steps/reviewAndImportStep'
import SelectDataStep from './_steps/selectDataStep'
import { WizardData } from './types'

const DEFAULT_CURRENCY = 'EUR'

interface Props {
  payrollPolicy: PayrollPolicyFragment
  payrollPolicyPeriod: PayrollPolicyPeriodFragment
  payrollSummaryImport: PayrollPayrollSummaryImport
  source: string
  onUpdateImportSummary?: (payrollSummaryImport: PayrollPayrollSummaryImport) => void
  onFinish?: () => void
  onClose?: () => void
  contractVersions: ContractVersions
}

const ImportSummaryWizard = ({
  payrollSummaryImport,
  payrollPolicy,
  payrollPolicyPeriod,
  source,
  onUpdateImportSummary,
  onFinish,
  onClose,
  contractVersions,
}: Props) => {
  const { employee, company, access } = useCurrentSession()

  const _currency =
    payrollPolicy?.currency ||
    employee?.legalEntity?.currency ||
    company?.currency ||
    DEFAULT_CURRENCY
  const currency: Currency = isCurrency(_currency) ? _currency : DEFAULT_CURRENCY

  // Wizard variables
  const initialData: WizardData = {
    payrollPolicy,
    payrollSummaryImport,
    payrollPolicyPeriod,
    source,
    employee: employee as EmployeesEmployee, // eslint-disable-line no-type-assertion/no-type-assertion
    company: company as ApiCoreCompany, // eslint-disable-line no-type-assertion/no-type-assertion
    access: access as ApiCoreAccess, // eslint-disable-line no-type-assertion/no-type-assertion
    currency,
    onUpdateImportSummary: (summaryImport: PayrollPayrollSummaryImport) => {
      onUpdateImportSummary?.(summaryImport)
    },
  }
  const [wizardData, setWizardData] = useState<WizardData>(initialData)

  useUnsafeEffect(() => {
    setWizardData(initialData)
  }, [])

  const selectDataCompleted = (data: WizardData) => {
    return (
      (data.worksheetMatches?.name !== undefined &&
        data.worksheetMatches?.last_name !== undefined &&
        data.worksheetMatches?.net_salary !== undefined) ||
      false
    )
  }

  const secondaryAction = {
    action: () => {
      onCloseWizard()
    },
    label: i18n.t('common.cancel'),
  }

  const selectDataStep: WizardStep<WizardData> = {
    title: i18n.t('payroll_bundle.payroll_summary_import.wizard.select_data_step.title'),
    hideSteps: true,
    isCompleted: (data) => selectDataCompleted(data),
    secondaryAction: secondaryAction,
    nextText: i18n.t('common.next'),
    render: (props) => <SelectDataStep {...props}></SelectDataStep>,
  }

  const mapToConceptsStep: WizardStep<WizardData> = {
    title: i18n.t('payroll_bundle.payroll_summary_import.wizard.map_to_concepts_step.title'),
    hideSteps: true,
    isCompleted: () => !wizardData.loadingContent,
    secondaryAction: secondaryAction,
    nextText: i18n.t('common.next'),
    render: (props) => <MapToConceptsStep {...props}></MapToConceptsStep>,
  }

  const reviewAndImportStep: WizardStep<WizardData> = {
    title: i18n.t('payroll_bundle.payroll_summary_import.wizard.review_and_import_step.title'),
    hideSteps: true,
    isCompleted: (data: WizardData) => {
      return (data?.employeesSelected?.size || 0) > 0
    },
    secondaryAction: secondaryAction,
    nextText: i18n.tp(
      'payroll_bundle.payroll_summary_import.wizard.review_and_import_step.import_button',
      { count: wizardData?.employeesSelected?.size || 0 }
    ),
    render: (props) => (
      <ReviewAndImportStep {...props} contractVersions={contractVersions}></ReviewAndImportStep>
    ),
  }

  const steps = [selectDataStep, mapToConceptsStep, reviewAndImportStep]

  const handleOnDataChanged = (data: WizardData) => {
    setWizardData(data)
  }

  const onOpenAutoFocus = () => {}

  const onFinishWizard = async () => {
    onFinish?.()
  }

  const onCloseWizard = () => {
    onClose?.()
  }

  const descriptionText = i18n.t('payroll_bundle.payroll_summary_import.wizard.description', {
    policy: payrollPolicy.name,
    period: payrollPolicyPeriod.name,
  })

  return (
    <Wizard.Modal
      locked
      title={i18n.t('payroll_bundle.payroll_summary_import.wizard.title')}
      description={descriptionText}
      initialData={initialData}
      icon="upload"
      onDataChanged={handleOnDataChanged}
      onSubmit={onFinishWizard}
      onClose={onCloseWizard}
      steps={steps}
      size="large"
      onOpenAutoFocus={onOpenAutoFocus}
    />
  )
}

export default ImportSummaryWizard
